<template>
  <BasicTemplate>
    <h3>
      <router-link to="/feeds">Feeds</router-link>
      /
      <router-link :to="'/feeds/' + $route.params.id">Feed #{{ $route.params.id }}</router-link>
      /
      Game #{{ $route.params.gameId }}
    </h3>

    <div class="game-form">
      <v-form class="mt-2">
        <div class="game-form__content-shell">
        <!-- Identity -->
        <v-card class="mb-4 game-form__section" elevation="1">
          <v-card-title class="game-form__section-title">Identity</v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  :value="game.slug"
                  disabled
                  dense
                  hide-details
                >
                  <template v-slot:label>
                    <field-help-label label="Slug" field="slug" />
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  :value="game.name"
                  disabled
                  dense
                  hide-details
                >
                  <template v-slot:label>
                    <field-help-label label="Name" field="name" />
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  :value="`${feed.name || ''} (${feed.environment || ''})`"
                  label="Feed"
                  disabled
                  dense
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="feedGame.gameVersionId"
                  :items="versions"
                  item-text="version"
                  item-value="id"
                  dense
                  hide-details
                >
                  <template v-slot:label>
                    <field-help-label label="Version" field="gameVersionId" />
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" md="8">
                <div class="game-form__field-with-action">
                  <v-autocomplete
                    v-model="feedGame.metaTemplateId"
                    :items="metaTemplates"
                    item-text="name"
                    item-value="id"
                    class="game-form__field-with-action-input"
                    dense
                    hide-details
                    clearable
                  >
                    <template v-slot:label>
                      <field-help-label label="Meta Template" field="metaTemplate" />
                    </template>
                  </v-autocomplete>
                  <v-btn
                    v-if="feedGame.metaTemplateId"
                    color="primary"
                    outlined
                    small
                    class="game-form__field-with-action-btn"
                    @click="$router.push(`/metadata-management/${feedGame.metaTemplateId}`)"
                  >
                    Meta Management
                  </v-btn>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Platform / auth / session -->
        <v-card class="mb-4 game-form__section" elevation="1">
          <v-card-title class="game-form__section-title">Platform / auth / session</v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" sm="6" md="3">
                <boolean-override-select
                  v-model="feedGame.hasAuth"
                  label="Auth"
                  field="hasAuth"
                  :items="selectStatuses"
                  :hint="effectiveBooleanHint('hasAuth')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <boolean-override-select
                  v-model="feedGame.hasEagle"
                  label="Eagle"
                  field="hasEagle"
                  :items="selectStatuses"
                  :hint="effectiveBooleanHint('hasEagle')"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Progress & persistence -->
        <v-card class="mb-4 game-form__section" elevation="1">
          <v-card-title class="game-form__section-title">Progress &amp; persistence</v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" sm="6" md="4" lg="3">
                <boolean-override-select
                  v-model="feedGame.hasInventory"
                  label="Inventory"
                  field="hasInventory"
                  :items="selectStatuses"
                  :hint="effectiveBooleanHint('hasInventory')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4" lg="3">
                <boolean-override-select
                  v-model="feedGame.hasCollection"
                  label="Collection"
                  field="hasCollection"
                  :items="selectStatuses"
                  :hint="effectiveBooleanHint('hasCollection')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4" lg="3">
                <boolean-override-select
                  v-model="feedGame.hasLeaderboard"
                  label="Leaderboard"
                  field="hasLeaderboard"
                  :items="selectStatuses"
                  :hint="effectiveBooleanHint('hasLeaderboard')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4" lg="3">
                <boolean-override-select
                  v-model="feedGame.hasMultiplayer"
                  label="Multiplayer"
                  field="hasMultiplayer"
                  :items="selectStatuses"
                  :hint="effectiveBooleanHint('hasMultiplayer')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4" lg="3">
                <boolean-override-select
                  v-model="feedGame.hasPlatformGameEnd"
                  label="Platform Game End"
                  field="hasPlatformGameEnd"
                  :items="selectStatuses"
                  :hint="effectiveBooleanHint('hasPlatformGameEnd')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4" lg="3">
                <boolean-override-select
                  v-model="feedGame.hasCloudSave"
                  label="Cloud Save"
                  field="hasCloudSave"
                  :items="selectStatuses"
                  :hint="effectiveBooleanHint('hasCloudSave')"
                />
              </v-col>
              <v-col v-if="showCloudSaveStorage" cols="12" sm="8" md="5" lg="5">
                <v-text-field
                  v-model="feedGame.cloudSaveStorage"
                  :hint="cloudSaveStorageHint"
                  persistent-hint
                  clearable
                  dense
                  @click:clear="feedGame.cloudSaveStorage = null"
                >
                  <template v-slot:label>
                    <field-help-label label="Cloud Save Storage" field="cloudSaveStorage" />
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Monetization -->
        <v-card class="mb-4 game-form__section" elevation="1">
          <v-card-title class="game-form__section-title">Monetization (MTX / ads)</v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" sm="6" md="4" lg="3">
                <boolean-override-select
                  v-model="feedGame.hasGems"
                  label="Gems"
                  field="hasGems"
                  :items="selectStatuses"
                  :hint="effectiveBooleanHint('hasGems')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4" lg="3">
                <boolean-override-select
                  v-model="feedGame.hasInterstitialAds"
                  label="Interstitial Ads"
                  field="hasInterstitialAds"
                  :items="selectStatuses"
                  :hint="effectiveBooleanHint('hasInterstitialAds')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4" lg="3">
                <boolean-override-select
                  v-model="feedGame.hasRewardedAds"
                  label="Rewarded Ads"
                  field="hasRewardedAds"
                  :items="selectStatuses"
                  :hint="effectiveBooleanHint('hasRewardedAds')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4" lg="3">
                <boolean-override-select
                  v-model="feedGame.isAdsFree"
                  label="Ads Free"
                  field="isAdsFree"
                  :items="selectStatuses"
                  :hint="effectiveBooleanHint('isAdsFree')"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model="feedGame.adLayoutCode" dense hide-details>
                  <template v-slot:label>
                    <field-help-label label="AD Layout Code" field="adLayoutCode" />
                  </template>
                </v-text-field>
              </v-col>
            </v-row>

            <div class="game-form__nested mt-2">
              <div class="game-form__nested-title">
                Feed Game Preroll Settings
              </div>
              <div class="game-form__nested-hint mb-2">
                Whole-entity override: set status to Enabled or Disabled to replace the game preroll entirely.
                Inherit removes the feed override (arena uses game preroll).
              </div>
              <v-row dense>
                <v-col cols="12" md="3">
                  <v-select
                    :items="prerollStatusItems"
                    v-model="prerollEnabledStatus"
                    item-text="text"
                    item-value="value"
                    dense
                    hide-details="auto"
                    persistent-hint
                    :hint="prerollOverrideHint"
                  >
                    <template v-slot:label>
                      <field-help-label label="Preroll status" field="gamePrerollSettings.isEnabled" />
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="12" sm="6" md="2">
                  <v-text-field
                    v-model="feedGamePrerollSettings.freePeriod"
                    type="number"
                    min="0"
                    dense
                    hide-details
                    :disabled="!isPrerollOverrideEnabled"
                  >
                    <template v-slot:label>
                      <field-help-label label="Free Period (days)" field="gamePrerollSettings.freePeriod" />
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <v-text-field
                    v-model="feedGamePrerollSettings.seconds"
                    type="number"
                    min="0"
                    dense
                    hide-details
                    :disabled="!isPrerollOverrideEnabled"
                  >
                    <template v-slot:label>
                      <field-help-label label="Duration (sec)" field="gamePrerollSettings.seconds" />
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="2">
                  <v-text-field
                    v-model="feedGamePrerollSettings.firstStartRate"
                    type="number"
                    min="0"
                    max="100"
                    dense
                    hide-details
                    :disabled="!isPrerollOverrideEnabled"
                  >
                    <template v-slot:label>
                      <field-help-label label="First Start Rate" field="gamePrerollSettings.firstStartRate" />
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="2">
                  <v-text-field
                    v-model="feedGamePrerollSettings.showRate"
                    type="number"
                    min="0"
                    max="100"
                    dense
                    hide-details
                    :disabled="!isPrerollOverrideEnabled"
                  >
                    <template v-slot:label>
                      <field-help-label label="Show Rate" field="gamePrerollSettings.showRate" />
                    </template>
                  </v-text-field>
                </v-col>
              </v-row>

              <v-alert
                type="info"
                outlined
                dense
                class="mt-2 game-level-fallback"
                icon="mdi-video-outline"
              >
                <div class="game-level-fallback__title">Game Level Fallback</div>
                <v-row dense class="game-level-fallback__row">
                  <v-col cols="12" sm="6" md="3" class="py-1">
                    <div class="game-level-fallback__field">
                      <div class="game-level-fallback__label">Preroll status</div>
                      <v-chip
                        small
                        label
                        :color="gameLevelPrerollSummary.status.chipColor"
                        :outlined="gameLevelPrerollSummary.status.outlined"
                      >
                        {{ gameLevelPrerollSummary.status.value }}
                      </v-chip>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="2" class="py-1">
                    <div class="game-level-fallback__field">
                      <div class="game-level-fallback__label">Free Period (days)</div>
                      <div
                        class="game-level-fallback__value"
                        :class="{ 'game-level-fallback__value--empty': gameLevelPrerollSummary.freePeriodEmpty }"
                      >
                        {{ gameLevelPrerollSummary.freePeriod }}
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="3" class="py-1">
                    <div class="game-level-fallback__field">
                      <div class="game-level-fallback__label">Duration (sec)</div>
                      <div
                        class="game-level-fallback__value"
                        :class="{ 'game-level-fallback__value--empty': gameLevelPrerollSummary.secondsEmpty }"
                      >
                        {{ gameLevelPrerollSummary.seconds }}
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="2" class="py-1">
                    <div class="game-level-fallback__field">
                      <div class="game-level-fallback__label">First Start Rate</div>
                      <div
                        class="game-level-fallback__value"
                        :class="{ 'game-level-fallback__value--empty': gameLevelPrerollSummary.firstStartRateEmpty }"
                      >
                        {{ gameLevelPrerollSummary.firstStartRate }}
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="2" class="py-1">
                    <div class="game-level-fallback__field">
                      <div class="game-level-fallback__label">Show Rate</div>
                      <div
                        class="game-level-fallback__value"
                        :class="{ 'game-level-fallback__value--empty': gameLevelPrerollSummary.showRateEmpty }"
                      >
                        {{ gameLevelPrerollSummary.showRate }}
                      </div>
                    </div>
                  </v-col>
                </v-row>
                <div class="game-level-fallback__hint">
                  Used when status is Inherit — arena gets the whole game preroll object (or nothing if game is Not set).
                </div>
              </v-alert>
            </div>
          </v-card-text>
        </v-card>

        <!-- Rendering & layout -->
        <v-card class="mb-4 game-form__section" elevation="1">
          <v-card-title class="game-form__section-title">Rendering &amp; layout</v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" sm="6" md="4" lg="3">
                <boolean-override-select
                  v-model="feedGame.isGRM"
                  label="Rendering in GRM"
                  field="isGRM"
                  :items="selectStatuses"
                  :hint="effectiveBooleanHint('isGRM')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4" lg="3">
                <boolean-override-select
                  v-model="feedGame.isIframe"
                  label="Rendering in IFrame"
                  field="isIframe"
                  :items="selectStatuses"
                  :hint="effectiveBooleanHint('isIframe')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4" lg="3">
                <boolean-override-select
                  v-model="feedGame.isFlex"
                  label="Flexible Size"
                  field="isFlex"
                  :items="selectStatuses"
                  :hint="effectiveBooleanHint('isFlex')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4" lg="3">
                <boolean-override-select
                  v-model="feedGame.hasFullscreen"
                  label="Full Screen"
                  field="hasFullscreen"
                  :items="selectStatuses"
                  :hint="effectiveBooleanHint('hasFullscreen')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4" lg="3">
                <boolean-override-select
                  v-model="feedGame.hasPortraitMode"
                  label="Portrait Mode"
                  field="hasPortraitMode"
                  :items="selectStatuses"
                  :hint="effectiveBooleanHint('hasPortraitMode')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4" lg="3">
                <v-text-field
                  v-model="feedGame.aspectRatio"
                  type="number"
                  dense
                  hide-details
                >
                  <template v-slot:label>
                    <field-help-label label="Aspect Ratio" field="aspectRatio" />
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Availability -->
        <v-card class="mb-4 game-form__section" elevation="1">
          <v-card-title class="game-form__section-title">Availability</v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" sm="6" md="3">
                <boolean-override-select
                  v-model="feedGame.hasComingSoon"
                  label="Coming Soon"
                  field="hasComingSoon"
                  :items="selectStatuses"
                  :hint="effectiveBooleanHint('hasComingSoon')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  type="date"
                  v-model="feedGame.comingSoonDate"
                  dense
                  hide-details
                >
                  <template v-slot:label>
                    <field-help-label label="Coming Soon Date" field="comingSoonDate" />
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <boolean-override-select
                  v-model="feedGame.isSubscriberOnly"
                  label="Subscriber Only"
                  field="isSubscriberOnly"
                  :items="selectStatuses"
                  :hint="effectiveBooleanHint('isSubscriberOnly')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  type="date"
                  v-model="feedGame.isSubscriberOnlyDate"
                  dense
                  hide-details
                >
                  <template v-slot:label>
                    <field-help-label label="Subscriber Only Date" field="isSubscriberOnlyDate" />
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Support Info -->
        <v-card class="mb-4 game-form__section" elevation="1">
          <v-card-title class="game-form__section-title">
            Support Info
          </v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" sm="4" md="2" lg="2">
                <v-select
                  :items="supportInfoStatusItems"
                  v-model="supportInfoActiveStatus"
                  item-text="text"
                  item-value="value"
                  dense
                  hide-details
                >
                  <template v-slot:label>
                    <field-help-label label="Status" field="supportInfo.isEnabled" />
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" sm="8" md="10" lg="10">
                <v-text-field
                  v-model="supportInfo.description"
                  dense
                  hide-details
                  :disabled="supportInfoActiveStatus !== 'enabled'"
                >
                  <template v-slot:label>
                    <field-help-label label="Description" field="supportInfo.description" />
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="supportInfo.emailText"
                  dense
                  hide-details
                  :disabled="supportInfoActiveStatus !== 'enabled'"
                >
                  <template v-slot:label>
                    <field-help-label label="Email Text" field="supportInfo.emailText" />
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="supportInfo.email"
                  dense
                  hide-details
                  :disabled="supportInfoActiveStatus !== 'enabled'"
                >
                  <template v-slot:label>
                    <field-help-label label="Email" field="supportInfo.email" />
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="supportInfo.phoneText"
                  dense
                  hide-details
                  :disabled="supportInfoActiveStatus !== 'enabled'"
                >
                  <template v-slot:label>
                    <field-help-label label="Phone Text" field="supportInfo.phoneText" />
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="supportInfo.phone"
                  dense
                  hide-details
                  :disabled="supportInfoActiveStatus !== 'enabled'"
                >
                  <template v-slot:label>
                    <field-help-label label="Phone" field="supportInfo.phone" />
                  </template>
                </v-text-field>
              </v-col>
            </v-row>

            <v-alert
              type="info"
              outlined
              dense
              class="mt-2 game-level-fallback"
              icon="mdi-gamepad-variant-outline"
            >
              <div class="game-level-fallback__title">Game Level Fallback</div>
              <v-row dense class="game-level-fallback__row">
                <v-col cols="12" sm="4" md="2" lg="2" class="py-1">
                  <div class="game-level-fallback__field">
                    <div class="game-level-fallback__label">Status</div>
                    <v-chip
                      small
                      label
                      :color="gameLevelSupportInfoSummary.status.chipColor"
                      :outlined="gameLevelSupportInfoSummary.status.outlined"
                    >
                      {{ gameLevelSupportInfoSummary.status.value }}
                    </v-chip>
                  </div>
                </v-col>
                <v-col cols="12" sm="8" md="10" lg="10" class="py-1">
                  <div class="game-level-fallback__field">
                    <div class="game-level-fallback__label">Description</div>
                    <div
                      class="game-level-fallback__value"
                      :class="{ 'game-level-fallback__value--empty': !gameLevelSupportInfoSummary.description }"
                    >
                      {{ gameLevelSupportInfoSummary.description || 'Not set' }}
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="3" class="py-1">
                  <div class="game-level-fallback__field">
                    <div class="game-level-fallback__label">Email Text</div>
                    <div
                      class="game-level-fallback__value"
                      :class="{ 'game-level-fallback__value--empty': !gameLevelSupportInfoSummary.emailText }"
                    >
                      {{ gameLevelSupportInfoSummary.emailText || 'Not set' }}
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="3" class="py-1">
                  <div class="game-level-fallback__field">
                    <div class="game-level-fallback__label">Email</div>
                    <div
                      class="game-level-fallback__value"
                      :class="{ 'game-level-fallback__value--empty': !gameLevelSupportInfoSummary.email }"
                    >
                      {{ gameLevelSupportInfoSummary.email || 'Not set' }}
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="3" class="py-1">
                  <div class="game-level-fallback__field">
                    <div class="game-level-fallback__label">Phone Text</div>
                    <div
                      class="game-level-fallback__value"
                      :class="{ 'game-level-fallback__value--empty': !gameLevelSupportInfoSummary.phoneText }"
                    >
                      {{ gameLevelSupportInfoSummary.phoneText || 'Not set' }}
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="3" class="py-1">
                  <div class="game-level-fallback__field">
                    <div class="game-level-fallback__label">Phone</div>
                    <div
                      class="game-level-fallback__value"
                      :class="{ 'game-level-fallback__value--empty': !gameLevelSupportInfoSummary.phone }"
                    >
                      {{ gameLevelSupportInfoSummary.phone || 'Not set' }}
                    </div>
                  </div>
                </v-col>
              </v-row>
              <div class="game-level-fallback__hint">
                Used when Status is Inherit.
              </div>
            </v-alert>
          </v-card-text>
        </v-card>

        <div class="game-form__save mb-2">
          <v-btn color="primary" large data-testid="feed-game-form-save" @click="saveFeedGame()">
            Save
          </v-btn>
        </div>
        </div>
      </v-form>
    </div>
  </BasicTemplate>
</template>

<script>
import BasicTemplate from '../../../templates/basic.vue';
import FieldHelpLabel from '../../../components/FieldHelpLabel/FieldHelpLabel.vue';
import BooleanOverrideSelect from '../../../components/FieldHelpLabel/BooleanOverrideSelect.vue';

const BOOLEAN_OVERRIDE_KEYS = [
  'hasAuth',
  'hasEagle',
  'hasInventory',
  'hasCollection',
  'hasLeaderboard',
  'hasMultiplayer',
  'hasPlatformGameEnd',
  'hasCloudSave',
  'hasGems',
  'hasInterstitialAds',
  'hasRewardedAds',
  'isAdsFree',
  'isGRM',
  'isIframe',
  'isFlex',
  'hasFullscreen',
  'hasPortraitMode',
  'hasComingSoon',
  'isSubscriberOnly',
];

export default {
  name: 'FeedGameEdit',
  components: {BasicTemplate, FieldHelpLabel, BooleanOverrideSelect},
  data() {
    return {
      activeTab: null,
      metaTemplates: [],
      feed: {},
      feedGame: {
        subscriberOnlyGame: null,
      },
      game: {},
      versions: [],
      selectStatuses: [
        {name: 'Inherit', value: null},
        {name: 'True', value: true},
        {name: 'False', value: false},
      ],
      feedGamePrerollSettings: {
        isEnabled: null,
        freePeriod: null,
        seconds: null,
        firstStartRate: null,
        showRate: null,
        feedGameId: null
      },
      supportInfo: {
        active: null,
        description: null,
        emailText: null,
        email: null,
        phoneText: null,
        phone: null,
        feedGameId: null
      }
    };
  },
  computed: {
    showCloudSaveStorage() {
      return this.effectiveBoolean('hasCloudSave') === true;
    },
    cloudSaveStorageHint() {
      const override = this.feedGame?.cloudSaveStorage;
      const gameValue = this.game?.cloudSaveStorage;
      if (override === null || override === undefined || override === '') {
        return `Inherit. Effective: ${gameValue || 'Not set'}`;
      }
      return `Override. Game default: ${gameValue || 'Not set'}`;
    },
    gameLevelPrerollSummary() {
      const s = this.game.gamePrerollSettings || {};
      const status = s.isEnabled === null || s.isEnabled === undefined
        ? { value: 'Not set', chipColor: 'grey', outlined: true }
        : s.isEnabled
          ? { value: 'Enabled', chipColor: 'success', outlined: false }
          : { value: 'Disabled', chipColor: 'grey darken-1', outlined: false };

      const display = (value) => (value === null || value === undefined || value === '' ? 'Not set' : String(value));
      const isEmpty = (value) => value === null || value === undefined || value === '';

      return {
        status,
        freePeriod: display(s.freePeriod),
        freePeriodEmpty: isEmpty(s.freePeriod),
        seconds: display(s.seconds),
        secondsEmpty: isEmpty(s.seconds),
        firstStartRate: display(s.firstStartRate),
        firstStartRateEmpty: isEmpty(s.firstStartRate),
        showRate: display(s.showRate),
        showRateEmpty: isEmpty(s.showRate),
      };
    },
    prerollStatusItems() {
      return [
        { text: 'Inherit', value: 'inherit' },
        { text: 'Enabled', value: 'enabled' },
        { text: 'Disabled', value: 'disabled' },
      ];
    },
    isPrerollOverrideEnabled() {
      return this.prerollEnabledStatus === 'enabled';
    },
    prerollOverrideHint() {
      if (this.prerollEnabledStatus === 'inherit') {
        return 'No feed override — arena uses the whole game preroll (or none).';
      }
      if (this.prerollEnabledStatus === 'disabled') {
        return 'Feed override: preroll disabled for this feed (game values ignored).';
      }
      return 'Feed override: all fields below replace the game preroll as one object.';
    },
    prerollEnabledStatus: {
      get() {
        // Whole-entity key: only isEnabled activates a feed override.
        if (this.feedGamePrerollSettings.isEnabled === null || this.feedGamePrerollSettings.isEnabled === undefined) {
          return 'inherit';
        }
        return this.feedGamePrerollSettings.isEnabled ? 'enabled' : 'disabled';
      },
      set(val) {
        if (val === 'inherit') {
          this.feedGamePrerollSettings.isEnabled = null;
          this.clearPrerollNumericFields();
        } else if (val === 'disabled') {
          this.feedGamePrerollSettings.isEnabled = false;
          this.clearPrerollNumericFields();
        } else {
          const wasEnabled = this.feedGamePrerollSettings.isEnabled === true;
          this.feedGamePrerollSettings.isEnabled = true;
          // Seed from game when entering Enabled override (whole-entity, not per-field merge).
          if (!wasEnabled) {
            this.seedPrerollFromGame();
          }
        }
      }
    },
    gameLevelSupportInfoSummary() {
      const s = this.game.supportInfo || {};
      const status = s.active === null || s.active === undefined
        ? { value: 'Not set', chipColor: 'grey', outlined: true }
        : s.active
          ? { value: 'Enabled', chipColor: 'success', outlined: false }
          : { value: 'Disabled', chipColor: 'grey darken-1', outlined: false };

      return {
        status,
        description: s.description || '',
        emailText: s.emailText || '',
        email: s.email || '',
        phoneText: s.phoneText || '',
        phone: s.phone || '',
      };
    },
    supportInfoStatusItems() {
      return [
        { text: 'Inherit', value: 'inherit' },
        { text: 'Enabled', value: 'enabled' },
        { text: 'Disabled', value: 'disabled' },
      ];
    },
    supportInfoActiveStatus: {
      get() {
        if (this.supportInfo.active === null) return 'inherit';
        return this.supportInfo.active ? 'enabled' : 'disabled';
      },
      set(val) {
        if (val === 'inherit') {
          this.supportInfo.active = null;
          // Clear values when inheriting
          this.supportInfo.description = null;
          this.supportInfo.emailText = null;
          this.supportInfo.email = null;
          this.supportInfo.phoneText = null;
          this.supportInfo.phone = null;
        } else if (val === 'disabled') {
          this.supportInfo.active = false;
        } else {
          this.supportInfo.active = val === 'enabled';
        }
      }
    }
  },
  async created() {
    await this.fetchData();
    await this.loadMetaTemplates();
  },
  methods: {
    convertEmptyToNull(value) {
      if (value === '' || value === undefined || value === null) return null;
      if (typeof value === 'string' && value.trim() === '') return null;
      return value;
    },
    effectiveBoolean(key) {
      const override = this.feedGame?.[key];
      if (override === null || override === undefined) {
        return this.game?.[key] ?? null;
      }
      return override;
    },
    formatBooleanEffective(value) {
      if (value === true) return 'true';
      if (value === false) return 'false';
      return 'not set';
    },
    effectiveBooleanHint(key) {
      const override = this.feedGame?.[key];
      const effective = this.effectiveBoolean(key);
      const gameValue = this.game?.[key];
      if (override === null || override === undefined) {
        return `Effective: ${this.formatBooleanEffective(effective)} (from game)`;
      }
      return `Effective: ${this.formatBooleanEffective(effective)} (game: ${this.formatBooleanEffective(gameValue)})`;
    },
    applySlp192FieldsFromApi(feedGameData) {
      if (!feedGameData || !this.feedGame) return;
      const keys = [
        'hasAuth',
        'hasPlatformGameEnd',
        'hasInventory',
        'hasCloudSave',
        'cloudSaveStorage',
        'hasMultiplayer',
        'hasInterstitialAds',
        'hasRewardedAds',
        'hasPortraitMode',
      ];
      keys.forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(feedGameData, key)) {
          this.$set(this.feedGame, key, feedGameData[key]);
        }
      });
    },
    async loadMetaTemplates() {
      try {
        const supportedGameId = this.feedGame?.game?.id || this.feedGame?.gameId;
        const gameId = this.game?.id || this.feedGame?.game?.id || this.feedGame?.gameId;
        const params = new URLSearchParams();
        if (supportedGameId) params.set('supportedGameId', String(supportedGameId));
        if (gameId) params.set('gameId', String(gameId));
        const query = params.toString();
        const url = query ? `/meta-templates?${query}` : '/meta-templates';
        this.metaTemplates = await this.$gameApi.get(url);
      } catch (e) {
        this.metaTemplates = [];
      }
    },
    async fetchData() {
      const {id, gameId} = this.$route.params;
      this.feed = await this.$gameApi.get(`/feeds/${id}?short=true`);
      this.feedGame = this.feed.feedGames.find(fg => fg.id == gameId);
      BOOLEAN_OVERRIDE_KEYS.forEach((key) => {
        if (!Object.prototype.hasOwnProperty.call(this.feedGame, key)) {
          this.$set(this.feedGame, key, null);
        }
      });
      if (!Object.prototype.hasOwnProperty.call(this.feedGame, 'cloudSaveStorage')) {
        this.$set(this.feedGame, 'cloudSaveStorage', null);
      }

      // Load preroll settings from the feed game endpoint
      try {
        const feedGameData = await this.$gameApi.get(`/feeds/${id}/games/${this.feedGame.gameId}`);
        this.applySlp192FieldsFromApi(feedGameData);

        if (feedGameData.feedGamePrerollSettings) {
          const fps = feedGameData.feedGamePrerollSettings;
          // Whole-entity key: without isEnabled the feed row is ignored — treat as inherit.
          const hasOverride = fps.isEnabled !== null && fps.isEnabled !== undefined;
          this.feedGamePrerollSettings = {
            isEnabled: hasOverride ? fps.isEnabled : null,
            freePeriod: hasOverride ? fps.freePeriod : null,
            seconds: hasOverride ? fps.seconds : null,
            firstStartRate: hasOverride ? fps.firstStartRate : null,
            showRate: hasOverride ? fps.showRate : null,
            feedGameId: fps.feedGameId ?? null
          };
        } else {
          // Reset to default values
          this.feedGamePrerollSettings = {
            isEnabled: null,
            freePeriod: null,
            seconds: null,
            firstStartRate: null,
            showRate: null,
            feedGameId: null
          };
        }

        if (feedGameData.supportInfo) {
          this.supportInfo = {
            active: feedGameData.supportInfo.active,
            description: feedGameData.supportInfo.description,
            emailText: feedGameData.supportInfo.emailText,
            email: feedGameData.supportInfo.email,
            phoneText: feedGameData.supportInfo.phoneText,
            phone: feedGameData.supportInfo.phone,
            feedGameId: feedGameData.supportInfo.feedGameId
          };
        } else {
          this.supportInfo = {
            active: null,
            description: null,
            emailText: null,
            email: null,
            phoneText: null,
            phone: null,
            feedGameId: null
          };
        }
      } catch (e) {
        // Reset to default values on error
        this.feedGamePrerollSettings = {
          isEnabled: null,
          freePeriod: null,
          seconds: null,
          firstStartRate: null,
          showRate: null,
          feedGameId: null
        };
        this.supportInfo = {
          active: null,
          description: null,
          emailText: null,
          email: null,
          phoneText: null,
          phone: null,
          feedGameId: null
        };
      }

      this.game = await this.$gameApi.get(`/games/${this.feedGame.game.id}`);
      this.versions = this.game.versions.concat({id: null, version: 'Latest'});
    },
    async editItem(item) {
      this.$router.push(`/feeds/${item.id}`);
    },
    async saveFeedGame() {
      try {
        this.feedGame.isSubscriberOnlyDate = this.feedGame.isSubscriberOnlyDate === '' ? null : this.feedGame.isSubscriberOnlyDate;
        this.feedGame.comingSoonDate = this.feedGame.comingSoonDate === '' ? null : this.feedGame.comingSoonDate;
        this.feedGame.cloudSaveStorage = this.convertEmptyToNull(this.feedGame.cloudSaveStorage);

        // Whole-entity preroll override (key = isEnabled):
        // inherit → null; disabled → { isEnabled: false }; enabled → full feed object.
        let prerollSettings;
        if (this.feedGamePrerollSettings) {
          prerollSettings = { ...this.feedGamePrerollSettings };

          prerollSettings.freePeriod = this.convertEmptyToNull(prerollSettings.freePeriod);
          prerollSettings.seconds = this.convertEmptyToNull(prerollSettings.seconds);
          prerollSettings.firstStartRate = this.convertEmptyToNull(prerollSettings.firstStartRate);
          prerollSettings.showRate = this.convertEmptyToNull(prerollSettings.showRate);

          const toNonNegInt = (v) => {
            if (v === null || v === undefined || v === '') return null;
            const n = Number(v);
            if (Number.isNaN(n)) return null;
            return Math.max(0, Math.floor(n));
          };
          prerollSettings.freePeriod = toNonNegInt(prerollSettings.freePeriod);
          prerollSettings.seconds = toNonNegInt(prerollSettings.seconds);
          prerollSettings.firstStartRate = toNonNegInt(prerollSettings.firstStartRate);
          prerollSettings.showRate = toNonNegInt(prerollSettings.showRate);
        } else {
          prerollSettings = {
            isEnabled: null,
            freePeriod: null,
            seconds: null,
            firstStartRate: null,
            showRate: null,
            feedGameId: this.feedGame.id
          };
        }

        if (prerollSettings.isEnabled === null || prerollSettings.isEnabled === undefined) {
          // Inherit — remove feed override so arena uses game preroll
          this.feedGame.feedGamePrerollSettings = null;
        } else if (prerollSettings.isEnabled === false) {
          this.feedGame.feedGamePrerollSettings = {
            feedGameId: this.feedGame.id,
            isEnabled: false,
            freePeriod: null,
            seconds: null,
            firstStartRate: null,
            showRate: null
          };
        } else {
          // Enabled — send entire feed object (null fields included; no per-field merge)
          prerollSettings.feedGameId = this.feedGame.id;
          this.feedGame.feedGamePrerollSettings = prerollSettings;
        }

        // Build supportInfo payload: inherit → null; disabled → active:false; enabled → full object
        let supportInfo;
        if (this.supportInfo) {
          supportInfo = { ...this.supportInfo };
          supportInfo.description = this.convertEmptyToNull(supportInfo.description);
          supportInfo.emailText = this.convertEmptyToNull(supportInfo.emailText);
          supportInfo.email = this.convertEmptyToNull(supportInfo.email);
          supportInfo.phoneText = this.convertEmptyToNull(supportInfo.phoneText);
          supportInfo.phone = this.convertEmptyToNull(supportInfo.phone);
        } else {
          supportInfo = {
            active: null,
            description: null,
            emailText: null,
            email: null,
            phoneText: null,
            phone: null,
            feedGameId: this.feedGame.id
          };
        }

        if (supportInfo.active === null) {
          this.feedGame.supportInfo = null;
        } else {
          supportInfo.feedGameId = this.feedGame.id;
          this.feedGame.supportInfo = supportInfo;
        }

        await this.$gameApi.patch(`/feeds/${this.feed.id}/games/${this.game.id}`, {
          ...this.feedGame,
          subscriberOnlyGame: this.feedGame.subscriberOnlyGame || null
        });

        this.$swal.fire({timer: 1500, icon: 'success', title: 'Feed game saved.'});
      } catch (e) {
        this.$swal.fire({ icon: 'error', title: e.statusCode, text: e.message });
      }
    },
    updatePrerollSetting(key, value) {
      this.feedGamePrerollSettings[key] = value === '' ? null : value;
    },
    clearPrerollNumericFields() {
      this.feedGamePrerollSettings.freePeriod = null;
      this.feedGamePrerollSettings.seconds = null;
      this.feedGamePrerollSettings.firstStartRate = null;
      this.feedGamePrerollSettings.showRate = null;
    },
    seedPrerollFromGame() {
      const g = this.game?.gamePrerollSettings || {};
      this.feedGamePrerollSettings.freePeriod = g.freePeriod ?? null;
      this.feedGamePrerollSettings.seconds = g.seconds ?? null;
      this.feedGamePrerollSettings.firstStartRate = g.firstStartRate ?? null;
      this.feedGamePrerollSettings.showRate = g.showRate ?? null;
    },
  }
};
</script>

<style scoped lang="scss">
.game-form {
  width: 100%;
}

.game-form__content-shell {
  border: 1px solid rgba(0, 0, 0, 0.22);
  border-radius: 8px;
  background: #fff;
  margin-bottom: 16px;
  padding: 16px 16px 8px;
}

.game-form__section {
  border-left: 4px solid #1976d2;
  overflow: hidden;
}

.game-form__section-title {
  display: flex;
  align-items: center;
  padding-top: 16px !important;
  padding-bottom: 12px !important;
  font-size: 1.15rem !important;
  font-weight: 600 !important;
  letter-spacing: 0.01em;
}

.game-form__section ::v-deep > .v-card-text {
  padding-top: 4px !important;
  padding-bottom: 12px !important;
}

.game-form__section ::v-deep .row--dense > .col,
.game-form__section ::v-deep .row--dense > [class*="col-"] {
  padding-top: 12px !important;
  padding-bottom: 12px !important;
}

.game-form__section ::v-deep .game-level-fallback__row.row--dense > .col,
.game-form__section ::v-deep .game-level-fallback__row.row--dense > [class*="col-"] {
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}

.game-form__field-with-action {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  min-width: 0;
}

.game-form__field-with-action-input {
  flex: 1 1 180px;
  min-width: 0;
}

.game-form__field-with-action-btn {
  flex: 0 0 auto;
}

.game-form__nested {
  padding: 16px;
  border: 1px solid #1976d2;
  border-radius: 8px;
  background: #fff;
}

.game-form__nested-title {
  display: flex;
  align-items: center;
  font-size: 0.95rem;
  font-weight: 600;
  color: #1565c0;
  margin-bottom: 12px;
}

.game-form__nested-hint {
  font-size: 0.8125rem;
  line-height: 1.35;
  color: rgba(0, 0, 0, 0.6);
}

.game-form__save {
  display: flex;
  justify-content: flex-start;
  position: sticky;
  bottom: 12px;
  z-index: 2;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(25, 118, 210, 0.2);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(6px);
}

.game-level-fallback {
  background: #f5f9ff !important;
  padding: 8px 12px !important;
  margin-bottom: 0 !important;

  &::v-deep .v-alert__wrapper {
    align-items: flex-start;
  }

  &::v-deep .v-alert__icon {
    margin-top: 2px;
    font-size: 18px !important;
    height: 18px;
    width: 18px;
    min-width: 18px;
  }

  &__title {
    font-size: 0.95rem;
    font-weight: 600;
    color: #1565c0;
    margin-bottom: 8px;
  }

  &__row {
    margin: 0 !important;
  }

  &__row.row--dense > .col,
  &__row.row--dense > [class*="col-"] {
    padding-top: 4px !important;
    padding-bottom: 4px !important;
  }

  &__field {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    min-height: 40px;
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(33, 150, 243, 0.18);
    border-radius: 6px;
    padding: 6px 10px;
  }

  &__label {
    flex: 0 0 auto;
    font-size: 0.8rem;
    font-weight: 500;
    color: #607d8b;
  }

  &__value {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 1rem;
    font-weight: 500;
    color: #263238;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &--empty {
      color: #90a4ae;
      font-style: italic;
      font-weight: 400;
    }
  }

  &__hint {
    margin-top: 6px;
    font-size: 0.8rem;
    color: #78909c;
  }
}
</style>
